/**
 * Building blocks of the administrator screens (iteration 2, package C6),
 * styled as `mockups/app-prototype.html`: page head, card, chip, pill
 * button, toast, a card for loading / errors / empty lists, and the role
 * gate (the API answers 403 to a client; the screen says why).
 * Tokens are the prototype's (phage.energy style: navy text, sky accent,
 * white cards 12 px, pill buttons), kept here so the three screens match it.
 */
import { Box, type SxProps, type Theme } from '@mui/material';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { AppShell } from '../../components/AppShell.js';
import { useProfile } from '../../hooks/use-profile.js';
import { useT } from '../../i18n/locale-context.js';
import type { MessageKey } from '../../i18n/messages.js';
import { ApiError } from '../../lib/api-client.js';
import { fill } from './admin-format.js';

export const ADMIN_TOKENS = {
  navy: '#04285d',
  sky: '#09a9f9',
  slate: '#66788f',
  line: '#e3ecf5',
  lineStrong: '#d3d9e3',
  bg: '#ffffff',
  errText: '#b42318',
  errBg: '#fdecea',
  errLine: '#f1c4c0',
  shadow: '0 4px 24px rgba(4, 40, 93, 0.08)',
} as const;
const T = ADMIN_TOKENS;

/** Prototype breakpoints: tables become cards at 640 px, chips shrink at 560 px. */
export const PHONE = '@media (max-width: 640px)';
export const NARROW = '@media (max-width: 560px)';

const FOCUS = { outline: `2px solid ${T.sky}`, outlineOffset: '2px' } as const;

export const subSx = { color: T.slate, fontSize: 13 } as const;

export const cardSx = {
  bgcolor: T.bg,
  border: `1px solid ${T.line}`,
  borderRadius: '12px',
  boxShadow: T.shadow,
  px: '24px',
  py: '20px',
  minWidth: 0,
  [NARROW]: { p: '16px' },
} as const;

export const h2Sx = { m: 0, fontSize: 18, fontWeight: 600, textWrap: 'balance' } as const;

/** Administrator page: toast, «h1 + sub» head, then the screen's sections. */
export function AdminPage({
  title,
  sub,
  toast,
  children,
}: {
  title: string;
  sub?: ReactNode;
  toast?: ReactNode;
  children: ReactNode;
}) {
  return (
    <AppShell>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          color: T.navy,
          lineHeight: 1.45,
          minWidth: 0,
        }}
      >
        <AdminOnly>
          {toast}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <Box component="h1" sx={{ m: 0, fontSize: 26, fontWeight: 600, textWrap: 'balance' }}>
              {title}
            </Box>
            {sub ? (
              <Box component="span" sx={subSx}>
                {sub}
              </Box>
            ) : null}
          </Box>
          {children}
        </AdminOnly>
      </Box>
    </AppShell>
  );
}

/** The administrator is role `operator` in the code; a client gets 403 from `/api/admin/*`. */
export function useIsAdmin(): boolean {
  const { data: profile } = useProfile();
  return profile?.role === 'operator';
}

function AdminOnly({ children }: { children: ReactNode }) {
  const t = useT();
  const isAdmin = useIsAdmin();
  if (isAdmin) return <>{children}</>;
  return (
    <StateCard
      title={t('operator.forbidden.title')}
      text={t('operator.forbidden.text')}
      action={<PillLink to="/app/sites">{t('operator.forbidden.back')}</PillLink>}
    />
  );
}

/** Empty list, loading, error: a card with a title, a line and an action. */
export function StateCard({ title, text, action }: { title: string; text?: string; action?: ReactNode }) {
  return (
    <Box
      component="section"
      aria-live="polite"
      sx={{ ...cardSx, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }}
    >
      <Box component="h2" sx={h2Sx}>
        {title}
      </Box>
      {text ? (
        <Box component="p" sx={{ ...subSx, m: 0 }}>
          {text}
        </Box>
      ) : null}
      {action}
    </Box>
  );
}

/** Pill: «На зв'язку», «Внутрішній арбітраж увімкнено»; `warn` — red. */
export function Chip({
  label,
  tone,
  dot,
  children,
}: {
  label?: string;
  tone?: 'warn';
  dot?: boolean;
  children?: ReactNode;
}) {
  const warn = tone === 'warn';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        px: '12px',
        py: '5px',
        borderRadius: '50px',
        border: `1px solid ${warn ? T.errLine : T.lineStrong}`,
        bgcolor: warn ? T.errBg : T.bg,
        color: warn ? T.errText : T.navy,
        fontSize: 12.5,
        [NARROW]: { fontSize: 12, px: '10px', py: '4px', borderRadius: '14px' },
      }}
    >
      {dot ? (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            width: 8,
            height: 8,
            flex: 'none',
            borderRadius: '50%',
            bgcolor: T.sky,
            boxShadow: '0 0 0 3px rgba(9, 169, 249, 0.18)',
          }}
        />
      ) : null}
      {label ? (
        <Box component="span" sx={{ color: T.slate }}>
          {label}
        </Box>
      ) : null}
      {children}
    </Box>
  );
}

const pillSx = (primary: boolean): SxProps<Theme> => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  px: '22px',
  py: '9px',
  borderRadius: '50px',
  cursor: 'pointer',
  border: `1px solid ${primary ? T.navy : T.lineStrong}`,
  bgcolor: primary ? T.navy : T.bg,
  color: primary ? '#ffffff' : T.navy,
  font: 'inherit',
  fontWeight: 500,
  textDecoration: 'none',
  boxShadow: '2px 4px 12px rgba(155, 159, 162, 0.09)',
  '&:focus-visible': FOCUS,
  '&:disabled': { cursor: 'default', opacity: 0.45 },
});

/** Pill button; `primary` — navy. */
export function PillButton({
  primary = false,
  ...props
}: ComponentPropsWithoutRef<'button'> & { primary?: boolean }) {
  return <Box component="button" type="button" sx={pillSx(primary)} {...props} />;
}

export function PillLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Box component={Link} to={to} sx={pillSx(true)}>
      {children}
    </Box>
  );
}

/** Navy notice at the top of the page; stays until closed (as the prototype). */
export function Toast({ text, onClose }: { text: string; onClose: () => void }) {
  const t = useT();
  return (
    <Box
      aria-live="polite"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        borderRadius: '12px',
        bgcolor: T.navy,
        color: '#ffffff',
        px: '16px',
        py: '12px',
        fontSize: 14,
      }}
    >
      <span>{text}</span>
      <Box
        component="button"
        type="button"
        aria-label={t('operator.close')}
        onClick={onClose}
        sx={{
          bgcolor: 'transparent',
          border: 0,
          color: '#ffffff',
          cursor: 'pointer',
          font: 'inherit',
          fontSize: 18,
          lineHeight: 1,
          '&:focus-visible': FOCUS,
        }}
      >
        ×
      </Box>
    </Box>
  );
}

/** Short reason of a failed request: no answer, no rights, session over, or the server's code. */
export function errorReason(err: unknown, t: (k: MessageKey) => string): string {
  if (!(err instanceof ApiError)) return t('operator.reason.offline');
  if (err.status === 401) return t('operator.reason.session');
  if (err.status === 403) return t('operator.reason.forbidden');
  return fill(t('operator.reason.server'), { status: err.status });
}

/** Loading / failed query: the same card for the three screens. */
export function QueryState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const t = useT();
  if (!error) return <StateCard title={t('operator.loading')} />;
  return (
    <StateCard
      title={fill(t('operator.load_error'), { reason: errorReason(error, t) })}
      action={<PillButton onClick={onRetry}>{t('operator.retry')}</PillButton>}
    />
  );
}
